import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  fetchComments, fetchTopic, fetchVoteState, postComment, voteTopic,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const useTopicData = (id) => {
  const { user, token, login } = useAuth();
  const [topic, setTopic] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myVote, setMyVote] = useState(null); // 'up' | 'down' | null
  const [voteBusy, setVoteBusy] = useState(false);
  const voteInFlight = useRef(false);

  const refreshComments = useCallback(async (topicId) => {
    const cs = await fetchComments(topicId);
    setComments(cs);
  }, [setComments]);

  const loadVoteState = useCallback(async (topicId) => {
    try {
      const v = await fetchVoteState(topicId, token);
      setMyVote(v.my_vote || null);
      setTopic((prev) => (prev ? { ...prev, upvotes: v.upvotes, downvotes: v.downvotes } : prev));
    } catch {
      setMyVote(null);
    }
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await fetchTopic(id);
      setTopic(t);
      await refreshComments(t.id);
      await loadVoteState(t.id);
    } catch {
      toast.error("File not found");
    } finally {
      setLoading(false);
    }
  }, [id, refreshComments, loadVoteState]);

  useEffect(() => { load(); }, [load]);

  // Re-fetch the user's vote state if the auth token changes (login/logout).
  useEffect(() => {
    if (topic?.id) loadVoteState(topic.id);
  }, [token, topic?.id, loadVoteState]);

  const vote = async (direction) => {
    if (!topic) return;
    if (voteInFlight.current) return;
    if (!user) {
      try { sessionStorage.setItem("archive_post_login_path",
          window.location.pathname + window.location.search); } catch { /* noop */ }
      login();
      return;
    }
    voteInFlight.current = true;
    setVoteBusy(true);
    try {
      const res = await voteTopic(topic.id, direction, token);
      setTopic((prev) => (prev ? { ...prev, upvotes: res.upvotes, downvotes: res.downvotes } : prev));
      setMyVote(res.my_vote || null);
    } catch {
      toast.error("Vote failed");
      // Reconcile from server so the UI never shows an inflated count.
      if (topic?.id) loadVoteState(topic.id);
    } finally {
      voteInFlight.current = false;
      setVoteBusy(false);
    }
  };

  const addComment = async ({ author, text, controversy_vote }) => {
    if (!topic) return;
    try {
      await postComment({
        topic_id: topic.id,
        author: author || "anonymous",
        text,
        controversy_vote,
      });
      await refreshComments(topic.id);
      toast.success("Filed to the record");
      return true;
    } catch {
      toast.error("Couldn't post comment");
      return false;
    }
  };

  return { topic, comments, loading, vote, addComment, myVote, voteBusy };
};
